import { Ionicons } from '@expo/vector-icons';
import type { ReactNode } from 'react';
import { Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Text } from '@/components/ui/text';
import { UserAvatar } from '@/components/ui/user-avatar';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/utils/format-time';
import type { PhotoCaption } from './image-viewer.types';

const AVATAR_SIZE = 32;

// The caption floats as an inset panel, like other floating chrome (toasts),
// rather than spanning the screen edge to edge. GUTTER is the gap to the screen
// edges; the safe-area insets are ADDED to it so the panel clears the Android
// nav bar / iOS home indicator and notches. The caption anchors itself
// absolutely, so a parent's padding can't move it: the insets must live here.
const GUTTER = 16;
// Caps the panel on wide web screens so the caption doesn't stretch across a
// desktop window; it centers within the viewer.
const PANEL_MAX_WIDTH = 640;

// Warm near-black panel (the same tone the old scrim faded to). Keeps caption
// text >=4.5:1 over any photo without a cool/neutral black (design system is
// all-warm).
const PANEL_BG = 'rgba(8,7,6,0.85)';
const CHEVRON_SIZE = 16;

/**
 * Rating ladder -> chip color, per the design system: `must_try` is the coral
 * apex, `try_it` the primary green, everything below reads muted. Text and
 * background classes are paired so the pill stays legible on the scrim.
 */
function ratingChipClasses(ratingKey: string | undefined): string {
  switch (ratingKey) {
    case 'must_try':
      return 'text-coral bg-coral/15';
    case 'try_it':
      return 'text-primary bg-primary/15';
    default:
      return 'text-muted-foreground bg-surface-secondary';
  }
}

interface ImageViewerCaptionProps {
  caption?: PhotoCaption;
  /** The photo's alt text; shown under the caption lines when present. */
  altText?: string | null;
  /**
   * Extra space to keep below the panel, for chrome drawn beneath it (the web
   * viewer's zoom bar). Added on top of the gutter and safe-area inset.
   */
  bottomOffset?: number;
  /**
   * Makes the panel a "View check-in" button. The viewer passes this only for
   * photos that belong to a check-in.
   */
  onPress?: () => void;
}

interface PanelFrameProps {
  onPress?: () => void;
  children: ReactNode;
}

/** The panel surface: a plain View, or a Pressable when `onPress` is set. */
function PanelFrame({ onPress, children }: PanelFrameProps) {
  const panelClass = 'w-full overflow-hidden rounded-xl px-4 py-3';
  const panelStyle = { maxWidth: PANEL_MAX_WIDTH, backgroundColor: PANEL_BG };

  if (!onPress) {
    return (
      <View testID="caption-panel" className={panelClass} style={panelStyle}>
        {children}
      </View>
    );
  }

  return (
    <Pressable
      testID="caption-panel"
      accessibilityRole="button"
      accessibilityLabel="View check-in"
      onPress={onPress}
      // Static style + `active:` class: NativeWind's Pressable interop drops a
      // `style={({ pressed }) => ...}` function on native.
      className={cn(panelClass, 'active:opacity-70')}
      style={panelStyle}
    >
      {children}
    </Pressable>
  );
}

/**
 * Bottom-anchored info bar rendered OVER a photo inside the viewer. Presentational
 * for assistive technology -- the viewer composes the real accessibility label via
 * `buildPhotoAccessibilityLabel`, so this bar must not be read on its own. The
 * one exception is the "View check-in" button the panel becomes with `onPress`.
 *
 * Degrades to nothing (returns null) when there is no dish name, author or alt
 * text, which is how business-photo galleries with no metadata show no bar.
 */
export function ImageViewerCaption({
  caption,
  altText,
  bottomOffset = 0,
  onPress,
}: ImageViewerCaptionProps) {
  const insets = useSafeAreaInsets();
  const dishName = caption?.dishName?.trim();
  const author = caption?.author;
  const authorName = author?.name?.trim();
  const alt = altText?.trim();
  const hasMeta = Boolean(dishName || authorName);

  if (!hasMeta && !alt) {
    return null;
  }

  const businessName = caption?.businessName?.trim();
  const ratingLabel = caption?.ratingLabel?.trim();

  // Primary line: the dish leads; if there is no dish, the author's name does.
  const primaryLabel = dishName || authorName;

  // Secondary line: "by {author}" only when the author is not already the
  // primary line, joined to the relative time with a middot when both exist.
  const secondaryParts: string[] = [];
  if (dishName && authorName) {
    secondaryParts.push(`by ${authorName}`);
  }
  if (caption?.createdAt !== undefined && caption?.createdAt !== null) {
    secondaryParts.push(formatRelativeTime(caption.createdAt));
  }
  const secondaryLine = secondaryParts.join(' · ');

  return (
    <View
      testID="caption-container"
      accessible={false}
      pointerEvents="box-none"
      className="absolute items-center"
      style={{
        left: GUTTER + insets.left,
        right: GUTTER + insets.right,
        bottom: GUTTER + insets.bottom + bottomOffset,
      }}
    >
      <PanelFrame onPress={onPress}>
        {hasMeta ? (
          <View className="flex-row items-center" style={{ gap: 12 }}>
            {author ? (
              <UserAvatar
                uri={author.avatarUrl}
                name={authorName}
                size={AVATAR_SIZE}
                decorative
              />
            ) : null}

            <View className="flex-1" style={{ gap: 2 }}>
              {/* RN Text defaults to flexShrink 0 (web: 1), so without these a
                  long name overflows the row and draws over the rating chip on
                  native. The business name takes whatever width is left; the
                  dish name keeps its natural width but can still truncate. */}
              <View
                testID="caption-title-row"
                className="flex-row items-center"
                style={{ gap: 6, minWidth: 0 }}
              >
                <Text
                  testID="caption-primary"
                  className="font-sans-semibold text-foreground"
                  style={{ flexShrink: 1 }}
                  numberOfLines={1}
                >
                  {primaryLabel}
                </Text>
                {businessName ? (
                  <Text
                    testID="caption-business"
                    className="text-sm text-muted-foreground"
                    style={{ flex: 1 }}
                    numberOfLines={1}
                  >
                    at {businessName}
                  </Text>
                ) : null}
              </View>

              {secondaryLine ? (
                <Text className="text-sm text-muted-foreground" numberOfLines={1}>
                  {secondaryLine}
                </Text>
              ) : null}
            </View>

            {ratingLabel ? (
              <Text
                testID="caption-rating-chip"
                className={cn(
                  'overflow-hidden rounded-full px-2.5 py-1 text-xs font-sans-semibold',
                  ratingChipClasses(caption?.ratingKey)
                )}
                numberOfLines={1}
              >
                {ratingLabel}
              </Text>
            ) : null}

            {onPress ? (
              <Ionicons
                testID="caption-chevron"
                name="chevron-forward"
                size={CHEVRON_SIZE}
                className="text-muted-foreground"
              />
            ) : null}
          </View>
        ) : null}

        {alt ? (
          <Text
            testID="caption-alt-text"
            className={cn('text-sm text-foreground', hasMeta && 'mt-2')}
            numberOfLines={3}
          >
            {alt}
          </Text>
        ) : null}
      </PanelFrame>
    </View>
  );
}
